import { useEffect } from 'react';
import { getKeyHandler } from 't-lib-ui/structure';
import { nativePlatform } from '../utils/platform';
import { AppleCredential } from '../connector/auth';
import './AppleLoginButton.css';

interface AppleLoginButtonProps {
  onSuccess: (credential: AppleCredential) => void;
  onError: (error: string) => void;
  label?: string;
  sideway?: boolean; // reached with left/right only (FlowForm 'sidewayFocus'), next to another button
}

const APPLE_AUTH_URL = 'https://appleid.apple.com/auth/authorize';
// sessionStorage keys shared with the auth server's callback page (aux-services/auth/handler/apple-callback.ts).
const RESULT_KEY = 'apple-auth', RETURN_KEY = 'apple-auth-return', STATE_KEY = 'apple-auth-state';

const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID || '';
export const appleLoginEnabled = !!appleClientId && !appleClientId.startsWith('<');

const redirectUri = () => `${window.location.origin}/auth/apple/callback`;

function takeSession(key: string): string | undefined {
  try {
    const value = sessionStorage.getItem(key) ?? undefined;
    sessionStorage.removeItem(key);
    return value;
  }
  catch {
    return undefined;
  }
}

// Web and Android app: Apple can only return the tokens of a sign-in that asks for the email by
// posting a form to the return URL, so the whole tab goes to Apple. Apple posts back to the auth
// server's callback page (on this origin, through the auth Worker), which stores the result in
// sessionStorage and returns to the page we came from; this component picks it up when it mounts
// there again. Nothing from Apple loads before the click.
// iOS app: the native sign-in sheet (cordova/local-plugins/apple-signin).
const AppleLoginButton: React.FC<AppleLoginButtonProps> = ({ onSuccess, onError, label = 'Sign in with Apple', sideway }) => {
  useEffect(() => {
    if (!appleLoginEnabled) return;
    const stored = takeSession(RESULT_KEY);
    const state = takeSession(STATE_KEY);
    takeSession(RETURN_KEY);
    if (!stored) return;
    const result = JSON.parse(stored);
    if (result.error == 'user_cancelled_authorize') return;
    if (result.error)
      onError(result.error);
    else if (!state || result.state != state)
      onError('Sign in with Apple: state mismatch');
    else
      onSuccess({ idToken: result.idToken, code: result.code, redirectUri: redirectUri() });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!appleLoginEnabled) return null;

  const handleClick = () => {
    if (nativePlatform == 'ios') {
      (window as any).cordova.plugins.appleSignIn.signIn()
        .then(onSuccess, (error: string) => error != 'cancelled' && onError(error));
      return;
    }
    const state = crypto.randomUUID();
    try {
      sessionStorage.setItem(STATE_KEY, state);
      sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search);
    }
    catch {
      return onError('Sign in with Apple needs session storage');
    }
    const params = new URLSearchParams({
      client_id: appleClientId,
      redirect_uri: redirectUri(),
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope: 'email', // never 'name': Downread doesn't store names
      state,
    });
    window.location.assign(`${APPLE_AUTH_URL}?${params}`);
  };

  const handleKeyDown = getKeyHandler(() => handleClick());

  return (
    <div className="appleLoginButton">
      <button className={`appleLoginCustomButton flowFocussable${sideway ? ' sidewayFocus' : ''}`} onClick={handleClick} onKeyDown={handleKeyDown} type="button">
        <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
        </svg>
        <span>{label}</span>
      </button>
    </div>
  );
};

export default AppleLoginButton;
